import { app } from '../app/state';
import {
  controlName,
  DEFAULT_PRIVACY,
  refDomain,
  sanitizePrivacy,
  screenBucket,
  type PrivacyChoice,
  type Snapshot,
} from '../core/analytics';

/**
 * First-party, cookieless-by-default usage stats (see PRIVACY.md).
 *
 * Everything is counted in memory for this page load and sent as one cumulative snapshot to
 * `/api/collect` on this same site — every minute while something changed, and when the page is
 * hidden or closed (sendBeacon). No third parties, no identifiers on the device. Only if the
 * visitor allows "remember this device" does the server set a single first-party cookie.
 */
const PRIVACY_KEY = 'pomodoro.privacy';
const BASE = import.meta.env.BASE_URL;
const IDLE_MS = 60_000; // "in use" = interaction within the last minute, or the timer running
const FLUSH_MS = 120_000; // ~1 database write per 2 minutes of use (see docs/ANALYTICS.md)
const SHORTCUTS = new Set([' ', 'r', 's', 'i', 'f', 't', 'n']);

let choice: PrivacyChoice = DEFAULT_PRIVACY;
let snap: Snapshot | null = null;
let lastSent = '';
let lastTick = 0;
let lastActivity = 0;
let tickTimer: ReturnType<typeof setInterval> | undefined;
let flushTimer: ReturnType<typeof setInterval> | undefined;
const listeners: ((c: PrivacyChoice) => void)[] = [];

/** The browser asks sites not to track (Global Privacy Control / Do Not Track): never ask, never remember. */
export const privacySignal = (): boolean =>
  (navigator as Navigator & { globalPrivacyControl?: boolean }).globalPrivacyControl === true ||
  navigator.doNotTrack === '1';

export const privacyChoice = () => choice;
export const onPrivacyChange = (fn: (c: PrivacyChoice) => void) => listeners.push(fn);

function loadChoice(): PrivacyChoice {
  try {
    return sanitizePrivacy(JSON.parse(localStorage.getItem(PRIVACY_KEY) ?? 'null'));
  } catch {
    return DEFAULT_PRIVACY;
  }
}

/** Updates the visitor's choice (stored on their device only). */
export function setPrivacy(next: Partial<PrivacyChoice>) {
  const prev = choice;
  choice = { ...choice, ...next };
  if (!choice.stats && choice.remember === 'yes') choice.remember = 'no'; // stats off forgets the device too
  try {
    localStorage.setItem(PRIVACY_KEY, JSON.stringify(choice));
  } catch {
    /* storage blocked: the choice lasts for this visit */
  }
  if (prev.remember === 'yes' && choice.remember !== 'yes') void post('api/consent', { remember: false });
  if (prev.remember !== 'yes' && choice.remember === 'yes') void post('api/consent', { remember: true });
  if (choice.stats && !prev.stats) start();
  if (!choice.stats && prev.stats) stop();
  listeners.forEach((fn) => fn(choice));
}

/* ---------- counters other modules report into ---------- */

export function track(feature: string, n = 1) {
  if (!snap) return;
  const k = controlName(feature);
  snap.feats[k] = (snap.feats[k] ?? 0) + n;
}

const focusListeners: ((k: 'start' | 'done' | 'break') => void)[] = [];
/** Lets the consent prompt wait for a natural moment (the first finished focus session). */
export const onFocusTracked = (fn: (k: 'start' | 'done' | 'break') => void) => focusListeners.push(fn);

export function trackFocus(kind: 'start' | 'done' | 'break', minutes = 0, interruptions = 0) {
  if (!snap) return;
  if (kind === 'start') snap.focusStarted++;
  else if (kind === 'break') snap.breaksDone++;
  else {
    snap.focusDone++;
    snap.focusMin += minutes;
    snap.interruptions += interruptions;
  }
  focusListeners.forEach((fn) => fn(kind));
}

export function trackTask(kind: 'added' | 'done') {
  if (!snap) return;
  if (kind === 'added') snap.tasksAdded++;
  else snap.tasksDone++;
}

/* ---------- collection ---------- */

const CONTROL = 'button, a, input, textarea, label, [role]';

/** A stable, text-free name for the control that was clicked. */
function controlFor(el: HTMLElement): string {
  // settings switches and steppers, and the task editor, are built without ids
  const named = el.closest<HTMLElement>('[data-sw], [data-step]');
  if (named?.dataset.sw) return `switch:${named.dataset.sw}`;
  if (named?.dataset.step && el.dataset.d)
    return `step:${named.dataset.step}:${el.dataset.d === '-1' ? 'less' : 'more'}`;
  if (el.closest('.t-editor')) {
    const part =
      el.dataset.d === '-1'
        ? 'fewer'
        : el.dataset.d
          ? 'more'
          : 'cancel' in el.dataset
            ? 'cancel'
            : el.matches('[type="submit"]')
              ? 'save'
              : el.matches('textarea')
                ? 'notes'
                : 'text';
    return `edit:${part}`;
  }
  if (el.id) return el.id;
  const d = el.dataset;
  if (d.p) return `preset:${d.p}`;
  if (d.tab) return `tab:${d.tab}`;
  if (d.a) return `ambient:${d.a}`;
  if (d.s) return `sound:${d.s}`;
  if (d.m) return `side:${d.m}m`;
  const cls = [...el.classList].find((c) => /^(t-|s-ctl|toast-|pm$|mini$|chip|nowtask|intbtn)/.test(c));
  return cls ?? el.tagName.toLowerCase();
}

function env(): Snapshot['env'] {
  const utm = new URLSearchParams(location.search).get('utm_source') ?? '';
  return {
    lang: (navigator.language || '').split('-')[0]!.toLowerCase(),
    screen: screenBucket(innerWidth),
    theme: document.documentElement.dataset.theme === 'dark' ? 'dark' : 'light',
    standalone: matchMedia('(display-mode: standalone)').matches,
    ref: refDomain(document.referrer, location.hostname),
    utm: utm
      .toLowerCase()
      .replace(/[^a-z0-9_.-]/g, '')
      .slice(0, 40),
  };
}

function fresh(): Snapshot {
  return {
    v: 1,
    sid: crypto.randomUUID(),
    remember: false,
    env: env(),
    openMs: 0,
    engagedMs: 0,
    clicks: 0,
    keys: 0,
    clicksBy: {},
    feats: {},
    focusStarted: 0,
    focusDone: 0,
    focusMin: 0,
    breaksDone: 0,
    interruptions: 0,
    tasksAdded: 0,
    tasksDone: 0,
    errors: 0,
    vitals: { lcp: 0, cls: 0, inp: 0 },
  };
}

function tick() {
  if (!snap) return;
  const now = performance.now();
  // hidden tabs tick about once a minute; a longer gap means the device slept
  const dt = Math.min(now - lastTick, 70_000);
  lastTick = now;
  snap.openMs += dt;
  const inUse = (!document.hidden && now - lastActivity < IDLE_MS) || app.data.run.running;
  if (inUse) snap.engagedMs += dt;
}

const activity = () => (lastActivity = performance.now());

function onClick(e: MouseEvent) {
  activity();
  if (!snap) return;
  const el = e.target instanceof Element ? e.target.closest<HTMLElement>(CONTROL) : null;
  // a click on a <label> is followed by a click on its input: count that one only
  if (el instanceof HTMLLabelElement && el.control && el.control !== e.target) return;
  snap.clicks++;
  if (el) {
    const k = controlName(controlFor(el));
    snap.clicksBy[k] = (snap.clicksBy[k] ?? 0) + 1;
  }
}

function onKey(e: KeyboardEvent) {
  activity();
  if (!snap || e.metaKey || e.ctrlKey || e.altKey || e.repeat) return;
  const t = e.target as HTMLElement;
  if (t.closest?.('input, textarea, select, [contenteditable="true"]')) return;
  const k = e.key.toLowerCase();
  if (!SHORTCUTS.has(k)) return;
  snap.keys++;
  track(`key:${k === ' ' ? 'space' : k}`);
}

const onError = () => snap && snap.errors++;

function observeVitals() {
  const v = () => snap?.vitals;
  const watch = (type: string, fn: (entries: PerformanceEntryList) => void, extra: object = {}) => {
    try {
      new PerformanceObserver((l) => fn(l.getEntries())).observe({
        type,
        buffered: true,
        ...extra,
      } as PerformanceObserverInit);
    } catch {
      /* not supported in this browser */
    }
  };
  watch('largest-contentful-paint', (es) => {
    const last = es[es.length - 1];
    if (last && v()) v()!.lcp = Math.round(last.startTime);
  });
  watch('layout-shift', (es) => {
    for (const e of es as (PerformanceEntry & { value: number; hadRecentInput: boolean })[])
      if (!e.hadRecentInput && v()) v()!.cls += e.value;
  });
  // interaction latency, approximated by the slowest input event of the visit
  watch(
    'event',
    (es) => {
      for (const e of es) if (v() && e.duration > v()!.inp) v()!.inp = Math.round(e.duration);
    },
    { durationThreshold: 40 },
  );
}

async function post(path: string, body: unknown) {
  try {
    await fetch(`${BASE}${path}`, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: typeof body === 'string' ? body : JSON.stringify(body),
      keepalive: true,
      credentials: 'same-origin',
    });
  } catch {
    /* offline: nothing is queued on the device */
  }
}

/** Sends the snapshot if it changed. `final` uses sendBeacon, which survives the page closing. */
function flush(final = false) {
  if (!snap || !choice.stats) return;
  tick();
  snap.remember = choice.remember === 'yes' && !privacySignal();
  snap.env.theme = document.documentElement.dataset.theme === 'dark' ? 'dark' : 'light';
  // re-send when counts changed, or after another minute of use
  const key = JSON.stringify({ ...snap, openMs: 0, engagedMs: Math.floor(snap.engagedMs / 60_000) });
  if (key === lastSent && !final) return;
  lastSent = key;
  const body = JSON.stringify({
    ...snap,
    vitals: { ...snap.vitals, cls: Math.round(snap.vitals.cls * 1000) / 1000 },
  });
  if (final && navigator.sendBeacon?.(`${BASE}api/collect`, new Blob([body], { type: 'application/json' })))
    return;
  void post('api/collect', body);
}

const onHide = () => document.visibilityState === 'hidden' && flush(true);
const onPageHide = () => flush(true);

function start() {
  if (snap) return;
  snap = fresh();
  lastTick = performance.now();
  activity();
  window.addEventListener('click', onClick, { capture: true, passive: true });
  window.addEventListener('keydown', onKey, { capture: true, passive: true });
  for (const ev of ['pointermove', 'wheel', 'touchstart'] as const)
    window.addEventListener(ev, activity, { passive: true });
  window.addEventListener('error', onError);
  window.addEventListener('unhandledrejection', onError);
  document.addEventListener('visibilitychange', onHide);
  window.addEventListener('pagehide', onPageHide);
  tickTimer = setInterval(tick, 1000);
  flushTimer = setInterval(() => flush(), FLUSH_MS);
  setTimeout(() => flush(), 5000); // the visit counts even if it's short
}

function stop() {
  window.removeEventListener('click', onClick, { capture: true });
  window.removeEventListener('keydown', onKey, { capture: true });
  for (const ev of ['pointermove', 'wheel', 'touchstart'] as const) window.removeEventListener(ev, activity);
  window.removeEventListener('error', onError);
  window.removeEventListener('unhandledrejection', onError);
  document.removeEventListener('visibilitychange', onHide);
  window.removeEventListener('pagehide', onPageHide);
  clearInterval(tickTimer);
  clearInterval(flushTimer);
  snap = null;
}

export function initAnalytics() {
  choice = loadChoice();
  observeVitals();
  if (choice.stats) start();
}
